import { BillingAudit } from '@/api/blackline/billing-audit';
import { TablePagination } from '@/components/ui/custom/table-pagination';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import {
	flexRender,
	getCoreRowModel,
	getPaginationRowModel,
	PaginationState,
	SortingState,
	useReactTable,
} from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';

import { getColumns } from './columns';

type Props = {
	data: BillingAudit[];
	totalCount: number;
	pagination: PaginationState;
	setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
	sorting: SortingState;
	setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
	isActiveTab: boolean;
	onSingleHide?: (id: number) => void;
	rowSelection: Record<string, boolean>;
	setRowSelection: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
	isFetching: boolean;
};

export function BlacklineBillingAuditTable({
	data,
	totalCount,
	pagination,
	setPagination,
	sorting,
	setSorting,
	isActiveTab = false,
	isFetching = false,
	onSingleHide,
	rowSelection,
	setRowSelection,
}: Props) {
	const table = useReactTable({
		data,
		columns: getColumns(onSingleHide, isActiveTab),
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		enableRowSelection: true,
		onRowSelectionChange: setRowSelection,
		getRowId: (row) => String(row.id),
		rowCount: totalCount,
		pageCount: Math.ceil(totalCount / pagination.pageSize),
		onPaginationChange: setPagination,
		manualPagination: true,
		manualSorting: true,
		onSortingChange: setSorting,
		state: {
			rowSelection,
			pagination,
			sorting,
		},
	});

	return (
		<>
			<div className="h-1/2 w-full">
				<div className="table-container relative rounded-md border">
					{isFetching && (
						<div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
							<Loader2 className="h-6 w-6 animate-spin text-gray-600" />
						</div>
					)}
					<div className="rounded-md border">
						<Table>
							<TableHeader>
								{table.getHeaderGroups().map((headerGroup) => (
									<TableRow key={headerGroup.id}>
										{headerGroup.headers.map((header) => (
											<TableHead key={header.id}>
												{header.isPlaceholder
													? null
													: flexRender(header.column.columnDef.header, header.getContext())}
											</TableHead>
										))}
									</TableRow>
								))}
							</TableHeader>
							<TableBody>
								{table.getRowModel().rows.length ? (
									table.getRowModel().rows.map((row) => (
										<TableRow key={row.id}>
											{row.getVisibleCells().map((cell) => (
												<TableCell key={cell.id}>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</TableCell>
											))}
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell
											colSpan={table.getAllColumns().length}
											className="py-10 text-center"
										>
											No results.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
				</div>

				{/* Footer */}
				<TablePagination table={table} isFetching={isFetching} />
			</div>
		</>
	);
}
